import { Link } from "react-router-dom";
import { Figure } from "./Figure";
import { ROUTES } from "../lib/routes";
import { DEPARTMENT_SHORT, type DistinguishedAlumnus } from "../lib/alumni";

/**
 * One tile in a distinguished-alumni roster. Shared by the Alumni page and the
 * department pages so the two cannot drift apart.
 *
 * A tile links to `${linkBase}/${slug}`: the Alumni page's own profiles by
 * default, a department's published profiles from a department page. An entry
 * with no slug renders as an article instead of a link, and .is-static drops
 * the hover lift that would imply otherwise.
 */
export function AlumnusCard({
  a,
  showDept,
  linkBase = ROUTES.alumni,
}: {
  a: DistinguishedAlumnus;
  showDept: boolean;
  linkBase?: string;
}) {
  const body = (
    <>
      <div className="photo">
        <Figure
          media={a.photo}
          fallbackLabel={"PORTRAIT · " + a.name.toUpperCase()}
          aspect="4/5"
          sizes="(max-width: 760px) 45vw, 220px"
        />
      </div>
      <div className="body">
        {(showDept || a.year) && (
          <div className="dist-meta">
            {showDept && (
              <span className="dist-dept">{DEPARTMENT_SHORT[a.department] ?? a.department}</span>
            )}
            {a.year && <span className="grad-year">Class of {a.year}</span>}
          </div>
        )}
        <h3>{a.name}</h3>
        <div className="role">{a.role}</div>
        {a.quote ? (
          <div className="quote">{a.quote}</div>
        ) : a.note ? (
          <div className="dist-note">{a.note}</div>
        ) : null}
      </div>
    </>
  );

  if (!a.slug) return <article className="dist-card is-static reveal">{body}</article>;
  return (
    <Link to={`${linkBase}/${a.slug}`} className="dist-card reveal">
      {body}
    </Link>
  );
}
